import { useEffect, useState, useContext } from "react";
import "./accountReferral.css";
import { CombinedContext } from "../../../context/combinedContext";
import billingRequest from "../../../request/billing";
import translations from "../../../translations/translations";
import { Gift, Copy, Share2, CheckCircle, Clock, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import AccountReferralSkeleton from "./accountReferralSkeleton/accountReferralSkeleton";

const REFERRAL_VALUE = 15;

const AccountReferral = () => {
	const { language, userData } = useContext(CombinedContext);

	const [referral, setReferral] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [copied, setCopied] = useState(null);

	const t = translations[language].account.referral;

	const role = userData?.user?.role;
	const isOwner = role === "owner";

	useEffect(() => {
		if (!userData?.user?.userId) return;
		loadReferral();
	}, [userData]);

	const loadReferral = async () => {
		try {
			const res = await billingRequest.getReferralDashboard(userData.user.userId, userData.cognitoTokens.AccessToken);
			setReferral(res);
		} catch (err) {
			console.error("Failed to load referral dashboard:", err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	const copyToClipboard = async (text, type) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(type);
			setTimeout(() => setCopied(null), 2000);
		} catch (err) {
			console.error("Clipboard copy failed:", err);
		}
	};

	const generateCode = async () => {
		try {
			await billingRequest.generateReferralCode({
				userId: userData.user.userId,
				accessToken: userData.cognitoTokens.AccessToken,
			});
			loadReferral();
		} catch (err) {
			console.error("Failed to generate referral code:", err);
		}
	};

	if (loading) return <AccountReferralSkeleton />;
	if (error || !referral) {
		return <div className="sf-referral-loading">Unable to load referral information</div>;
	}

	const referralsIssued = referral.referralsIssued || referral.referrals || [];
	const referralReceived = referral.referralReceived || null;
	const successfulIssued = referralsIssued.filter((r) => r.status === "reward_issued");
	const pendingIssued = referralsIssued.filter((r) => r.status !== "reward_issued");
	const totalSavings = successfulIssued.length * REFERRAL_VALUE;
	const referralLink = referral.referralCode ? `https://reservasinfila.com/listyourshop/${referral.referralCode}` : null;

	return (
		<div className="sf-referral-page">
			{/* ── HERO ── */}
			<div className="sf-referral-hero">
				<div className="sf-referral-hero-icon">
					<Gift size={22} />
				</div>
				<h2 className="sf-referral-title">
					{t.hero.title.replace("$15", "")}
					<span> $15 USD</span>
				</h2>
				<p className="sf-referral-subtitle">{t.hero.subtitle}</p>
				{totalSavings > 0 && <div className="sf-referral-earned">{t.hero.earned(totalSavings)}</div>}
			</div>

			{/* ── YOUR CODE + LINK ── */}
			<div className="sf-referral-card">
				<div className="sf-referral-label">{t.code.label}</div>

				{referral.referralCode ? (
					<>
						<div className="sf-referral-box">
							<span>{referral.referralCode}</span>
							<button onClick={() => copyToClipboard(referral.referralCode, "code")} className={copied === "code" ? "copied" : ""}>
								<Copy size={15} />
							</button>
						</div>

						<div className="sf-referral-label">{t.link.label}</div>

						<div className="sf-referral-box">
							<span className="sf-referral-link-text">{referralLink}</span>
							<button onClick={() => copyToClipboard(referralLink, "link")} className={copied === "link" ? "copied" : ""}>
								<Share2 size={15} />
							</button>
						</div>
					</>
				) : (
					<>
						<div className="sf-empty-small">{translations[language].billing?.referral?.noCode}</div>
						<div className="sf-billing-referral-button-div">
							<button className="sf-btn sf-btn-primary sf-billing-referral-code-button" onClick={generateCode}>
								{translations[language].billing?.referral?.generate}
							</button>
						</div>
					</>
				)}

				<div className="sf-referral-hint">{t.hint}</div>
			</div>
			{isOwner && (
				<div>
					{/* ── REFERRALS ISSUED (shops I referred) ── */}
					<div className="sf-referral-card">
						<div className="sf-referral-history-header">
							<div className="sf-referral-section-title">
								<ArrowUpRight size={15} />
								{t.history.title || "Shops You Referred"}
							</div>
							{referralsIssued.length > 0 && (
								<div className="sf-referral-history-stats">
									{successfulIssued.length > 0 && (
										<span className="sf-referral-stat">
											<CheckCircle size={11} />
											{successfulIssued.length} active
										</span>
									)}
									{pendingIssued.length > 0 && (
										<span className="sf-referral-stat sf-referral-stat-pending">
											<Clock size={11} />
											{pendingIssued.length} pending
										</span>
									)}
								</div>
							)}
						</div>

						{referralsIssued.length > 0 ? (
							referralsIssued.map((r, i) => (
								<div key={i} className="sf-referral-history-item">
									<div className="sf-referral-history-left">
										<span className="sf-referral-history-name">{r.invitedShopName || r.refereeEmail || "New shop"}</span>
										{r.refereeEmail && r.invitedShopName && <span className="sf-referral-history-email">{r.refereeEmail}</span>}
										{r.createdAt && <span className="sf-referral-history-date">{new Date(r.createdAt).toLocaleDateString()}</span>}
									</div>
									<div className="sf-referral-history-right">
										<span className={`sf-badge sf-badge-${r.status || "pending"}`}>
											{t.statusLabels?.[r.status] || t.history?.rewarded || r.status}
										</span>
										{r.rewardIssued && <span className="sf-referral-reward-pill">+${REFERRAL_VALUE} earned</span>}
									</div>
								</div>
							))
						) : (
							<div className="sf-referral-empty">{t.history?.empty}</div>
						)}
					</div>

					{/* ── REFERRAL RECEIVED (code I used) ── */}
					<div className="sf-referral-card">
						<div className="sf-referral-section-title">
							<ArrowDownLeft size={15} />
							{t.received?.title || "Referral You Used"}
						</div>

						{referralReceived ? (
							<div className="sf-referral-history-item">
								<div className="sf-referral-history-left">
									<span className="sf-referral-history-name">
										{referralReceived.referrerShopName || referralReceived.referrerEmail || "Referrer"}
									</span>
									{referralReceived.referrerEmail && referralReceived.referrerShopName && (
										<span className="sf-referral-history-email">{referralReceived.referrerEmail}</span>
									)}
									<div className="sf-referral-used-code-row" style={{ marginTop: 4 }}>
										<span className="sf-code-pill">{referralReceived.code}</span>
									</div>
									{referralReceived.createdAt && (
										<span className="sf-referral-history-date">{new Date(referralReceived.createdAt).toLocaleDateString()}</span>
									)}
								</div>
								<div className="sf-referral-history-right">
									<span className={`sf-badge sf-badge-${referralReceived.rewardIssued ? "active" : "pending"}`}>
										{referralReceived.rewardIssued
											? t.history?.rewarded || "Rewarded"
											: t.statusLabels?.[referralReceived.status] || referralReceived.status || "Pending"}
									</span>
								</div>
							</div>
						) : (
							<div className="sf-referral-empty">{t.received?.empty || "You haven't used a referral code yet"}</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default AccountReferral;
